/**
 * ExplainThis — small inline "(?)" popover that decodes security jargon
 * for non-technical reviewers (procurement, legal, finance leads).
 *
 * Why this exists
 * ───────────────
 * The Trust Center surfaces lots of cryptographic concepts — Bundle
 * SHA, Audit-chain head, PGP fingerprint, tracking tokens. Most users
 * who land here are not engineers; they're CISOs' assistants, GRC
 * analysts, or procurement leads. A small "[Explain this]" link
 * lowers the barrier to trust by demystifying the terminology without
 * cluttering the primary UI.
 *
 * Usage
 * ─────
 *   import ExplainThis, { EXPLAINERS } from '@/components/ExplainThis';
 *
 *   <span>Bundle SHA <ExplainThis topic="bundle-sha" /></span>
 *
 * Pass `topic` (preferred — pulls from the curated EXPLAINERS map) or
 * pass `title` + `body` ad-hoc.
 */
import React from 'react';
import { HelpCircle } from 'lucide-react';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';

export const EXPLAINERS = {
  'bundle-sha': {
    title: 'What is a Bundle SHA?',
    body: (
      <>
        <p>
          Modern web apps ship hundreds of JavaScript files bundled into one
          optimised file. A <strong>SHA</strong> (Secure Hash Algorithm) is a
          cryptographic <em>fingerprint</em> of that bundle — change a single
          character and the fingerprint changes entirely.
        </p>
        <p className="mt-2">
          The page shows two values:
        </p>
        <ul className="mt-1 list-disc pl-5 space-y-1">
          <li>
            <strong>Expected</strong> — the official fingerprint we
            pre-calculated when we published this pack, embedded in the page
            metadata.
          </li>
          <li>
            <strong>Derived</strong> — the fingerprint your browser
            calculated <em>after</em> downloading the bundle.
          </li>
        </ul>
        <p className="mt-2">
          A match proves the file you downloaded is byte-identical to the one
          we published — defeating tampering on compromised Wi-Fi, malicious
          CDN swaps, and skimmer-script injection. Think of it as a
          tamper-evident seal on a medication bottle.
        </p>
      </>
    ),
  },
  'sha256': {
    title: 'What is SHA-256?',
    body: (
      <>
        <p>
          A cryptographic hash function. Feed it any input — a file, a
          paragraph, a binary blob — and it spits out a 64-character
          hexadecimal fingerprint. Two different inputs effectively never
          produce the same SHA-256 (collision requires more compute than the
          age of the universe).
        </p>
        <p className="mt-2">
          We use SHA-256 to fingerprint every document in this pack. If
          anyone tampers with the file after we published it — even a
          one-character change — the SHA-256 won&apos;t match. You verify the
          fingerprint client-side, with no server in the loop.
        </p>
      </>
    ),
  },
  'audit-head': {
    title: 'What is the Audit-chain head?',
    body: (
      <>
        <p>
          The latest entry in our tamper-evident audit log. Every privileged
          action — admin login, role change, document upload — adds a row to
          this log. Each row carries the hash of the <em>previous</em> row,
          forming an unbroken chain.
        </p>
        <p className="mt-2">
          An attacker who rewrites history would have to recompute every
          downstream hash to cover their tracks, which is computationally
          infeasible without the keys.
        </p>
        <p className="mt-2">
          Publishing the &quot;head&quot; (the most recent hash) pins this
          pack to a specific moment in our live system. A public verifier at{' '}
          <code className="text-[11px] font-mono">/api/security/audit/verify/public</code>{' '}
          walks the chain and surfaces any breakage.
        </p>
      </>
    ),
  },
  'pgp': {
    title: 'What is a PGP fingerprint?',
    body: (
      <>
        <p>
          PGP (Pretty Good Privacy) is a long-standing cryptographic standard
          for signing files. A fingerprint is a short identifier — 20 bytes,
          displayed as 10 pairs of hex characters — that uniquely names a
          signing key.
        </p>
        <p className="mt-2">
          If you receive a countersigned copy of this pack from us, the
          signing key&apos;s fingerprint must match the one printed here.
          Mismatched fingerprint = the wrong key was used or the pack has
          been forged. Our public key lives at{' '}
          <code className="text-[11px] font-mono">/.well-known/security-pubkey.asc</code>.
        </p>
      </>
    ),
  },
  'tracking-token': {
    title: 'What is a tracking token?',
    body: (
      <>
        <p>
          A unique short identifier (format: <code className="text-[11px] font-mono">TKN-XXXXXXXXXXXX</code>)
          embedded in every per-prospect copy of this pack. The token appears
          as a visible watermark on every page and inside the PDF&apos;s
          metadata.
        </p>
        <p className="mt-2">
          If a copy of this pack later surfaces outside its intended
          recipient&apos;s organisation, we can trace it back to the original
          prospect via the token — like a watermark on a banknote. The
          standard public pack has no token; only personalised copies do.
        </p>
      </>
    ),
  },
  'sub-processors': {
    title: 'What is a sub-processor?',
    body: (
      <>
        <p>
          Any third-party service we use that handles your data — payments,
          email, cloud storage, CDN, etc. GDPR Article 28 requires we
          publish a complete list. This table is that list.
        </p>
        <p className="mt-2">
          <strong>Tier</strong> indicates risk severity:
        </p>
        <ul className="mt-1 list-disc pl-5 space-y-1">
          <li><strong>T1</strong> — high-impact / regulated (Stripe, AWS)</li>
          <li><strong>T2</strong> — medium-impact (Hostinger email)</li>
          <li><strong>T3</strong> — low-impact / edge-only (Cloudflare)</li>
        </ul>
        <p className="mt-2">
          <strong>Residency</strong> is where data is stored at rest.{' '}
          <strong>DPA</strong> means we have a signed Data Processing
          Addendum on file.{' '}
          <strong>SOC 2</strong> is an industry security certification.
        </p>
      </>
    ),
  },
};

export default function ExplainThis({ topic, title, body, label = '?', side = 'bottom', className = '' }) {
  const entry = topic ? EXPLAINERS[topic] : { title, body };
  if (!entry || !entry.body) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`explain-this-${topic || 'custom'}`}
          aria-label={`Explain: ${entry.title}`}
          className={`inline-flex items-center justify-center align-middle h-4 w-4 ml-1 rounded-full border border-current opacity-60 hover:opacity-100 transition-opacity text-[10px] font-bold cursor-help ${className}`}
        >
          {label === '?' ? <HelpCircle size={10} strokeWidth={2.2} /> : label}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align="start"
        className="w-80 p-4 text-[12.5px] leading-relaxed bg-zinc-900 text-zinc-100 border-zinc-700 shadow-xl"
        data-testid={`explain-this-content-${topic || 'custom'}`}
      >
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-1.5" style={{ color: '#D4AF37' }}>
          [Plain English]
        </div>
        <div className="font-bold text-sm mb-2 text-white">{entry.title}</div>
        <div className="text-zinc-300">{entry.body}</div>
      </PopoverContent>
    </Popover>
  );
}
